var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/fetch-as-promise.ts
import {
  LeagueSocketAsPromised
} from "@leagueplatform/league-socket";
import { Mutex } from "async-mutex";
import * as uuid from "uuid";
import {
  captureError,
  finishSpan,
  startSpanOnActiveTransaction,
  combineErrorContexts
} from "@leagueplatform/observability";

// src/util/get-thrown-error-message.ts
function getThrownErrorMessage(error) {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

// src/util/get-user-configure-connection.ts
import jstz from "jstz";
import { DEFAULT_LOCALE, getCurrentLocale } from "@leagueplatform/locales";
function getUserConfigureConnection() {
  const locale = getCurrentLocale(DEFAULT_LOCALE);
  return {
    message_type: "configure_connection",
    info: { tz_name: jstz.determine().name(), client_platform: "web", locale }
  };
}

// src/fetch-as-promise.ts
function isErrorMessage(message) {
  var _a;
  if (!message)
    return true;
  if (message.message_type === "fail")
    return true;
  if (message.message_type === "server_error")
    return true;
  if (((_a = message == null ? void 0 : message.info) == null ? void 0 : _a.code) === "invalid_request")
    return true;
  return false;
}
var _mutex;
var FetchAsPromise = class extends LeagueSocketAsPromised {
  constructor(url, options = { anonymous: false }) {
    super(url);
    __privateAdd(this, _mutex, new Mutex());
    this.registerUnload = () => {
      this.unloadHandler = () => __async(this, null, function* () {
        yield this.close();
        window.removeEventListener("unload", this.unloadHandler);
      });
      window.addEventListener("unload", this.unloadHandler);
    };
    this.options = options;
  }
  configureConnection() {
    return __async(this, null, function* () {
      const configMessage = getUserConfigureConnection();
      yield this.sendRequest(configMessage, {
        requestId: uuid.v4()
      });
    });
  }
  /**
   * Extend superclass with a fetch-like interface that applies a unique message
   * ID and throws errors given in the response. It also handles waiting for
   * the socket to open so that consumers don't have to remember to do that.
   */
  fetch(_0) {
    return __async(this, arguments, function* (message, fetchOptions = {}) {
      var _a, _b, _c;
      const span = startSpanOnActiveTransaction({
        /**
         * we set the op to the same value that Sentry's
         * built-in instrumentation sets it when it creates
         * a `fetch` span. This will allow us to query for aggregate
         * data about API-related spans that incldues both WS calls and
         * REST calls.
         */
        op: "http.client",
        description: message.message_type,
        tags: {
          isWS: true
        }
      });
      let response;
      const { errorContext: optionalErrorContext = {} } = fetchOptions;
      try {
        yield __privateGet(this, _mutex).runExclusive(() => __async(this, null, function* () {
          if (!this.isOpened || this.isOpening) {
            yield this.open();
            this.registerUnload();
            if (!this.options.anonymous) {
              yield this.authenticate();
            }
            yield this.configureConnection();
          }
          if (!this.options.anonymous) {
            yield this.authenticateIfNeeded();
          }
        }));
        response = yield this.sendRequest(message, {
          requestId: uuid.v4()
        });
      } catch (error) {
        const errorMessage = getThrownErrorMessage(error);
        const fetchError = new Error(
          `{ "info": { "reason": "${errorMessage}" } }`
        );
        const fetchErrorContext = combineErrorContexts([
          { errorName: "Socket Fetch Error" },
          optionalErrorContext
        ]);
        captureError(fetchError, fetchErrorContext);
        if (span)
          finishSpan(span);
        throw fetchError;
      }
      if (span)
        finishSpan(span);
      if (isErrorMessage(response)) {
        const messageType = (_b = (_a = response.info) == null ? void 0 : _a.message) == null ? void 0 : _b.message_type;
        const fetchErrorContext = combineErrorContexts([
          {
            errorName: `Socket Fetch Response Error: ${messageType}`,
            tags: {
              messageType
            }
          },
          optionalErrorContext
        ]);
        captureError(Error((_c = response.info) == null ? void 0 : _c.reason), fetchErrorContext);
        throw Error(JSON.stringify(response));
      }
      return response == null ? void 0 : response.info;
    });
  }
};
_mutex = new WeakMap();

// src/socket-as-fetch.ts
var _instance;
var SocketAsFetchClass = class {
  constructor({ anonymous = false } = {}) {
    __privateAdd(this, _instance, void 0);
    this.anonymous = anonymous;
  }
  init(url) {
    if (__privateGet(this, _instance)) {
      console.warn(`SocketAsFetch has already been initialized with ${url}.`);
    }
    if (!__privateGet(this, _instance)) {
      __privateSet(this, _instance, new FetchAsPromise(url, { anonymous: this.anonymous }));
    }
  }
  fetch(message, options) {
    return __async(this, null, function* () {
      if (!__privateGet(this, _instance)) {
        throw Error("SocketAsFetch needs to be initialized before use.");
      }
      return __privateGet(this, _instance).fetch(message, options);
    });
  }
};
_instance = new WeakMap();
var SocketAsFetch = new SocketAsFetchClass();
var SocketAsFetchAnonymous = new SocketAsFetchClass({
  anonymous: true
});
export {
  SocketAsFetch,
  SocketAsFetchAnonymous
};
//# sourceMappingURL=index.js.map